// =============================================================================
// PORTAL AUTH CONTEXT (PortalAuthContext.js)
// =============================================================================
// Manages authentication state for the portal tab.
// Adapted from cdic-portal-react/client/src/context/AuthContext.js
//
// KEY CHANGE: Import path points to local portalApi.js instead of ../services/api
// =============================================================================

import React, { createContext, useState, useEffect, useContext } from 'react';
import { checkSession, login as apiLogin, logout as apiLogout } from './portalApi';

const PortalAuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in when component mounts
  useEffect(() => {
    async function checkAuth() {
      try {
        const data = await checkSession();
        if (data.isAuthenticated) {
          setUser(data.user);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Portal session check failed:', error);
      } finally {
        setLoading(false);
      }
    }
    checkAuth();
  }, []);

  const login = async (username, password) => {
    const data = await apiLogin(username, password);
    if (data.success) {
      setUser(data.user);
      setIsAuthenticated(true);
    }
    return data;
  };

  const logout = async () => {
    await apiLogout();
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <PortalAuthContext.Provider value={{
      user,
      isAuthenticated,
      loading,
      login,
      logout,
      setUser,
      setIsAuthenticated,
    }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a portal AuthProvider');
  }
  return context;
}
